import classNames from 'classnames';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useState } from 'react';
import { ReactComponent as ContractorIcon } from '../../../../../../../../../img/contractor.svg';
import { ReactComponent as AttachmentsIcon } from '../../../../../../../../../img/icon-attachments.svg';
import { ReactComponent as CompletedIcon } from '../../../../../../../../../img/icon-done.svg';
import { ReactComponent as DynamicFormDataIcon } from '../../../../../../../../../img/icon-dynamic-form-data.svg';
import { ReactComponent as DynamicFormIcon } from '../../../../../../../../../img/icon-dynamic-form.svg';
import { ReactComponent as NoteIcon } from '../../../../../../../../../img/icon-note.svg';
import { ReactComponent as OverdueIcon } from '../../../../../../../../../img/icon-overdue.svg';
import { ReactComponent as PauseIcon } from '../../../../../../../../../img/icon-pause.svg';
import { ReactComponent as PlayIcon } from '../../../../../../../../../img/icon-play.svg';
import { ReactComponent as ProgressIcon } from '../../../../../../../../../img/icon-progress.svg';
import { ReactComponent as ResolveIcon } from '../../../../../../../../../img/icon-resolve.svg';
import { ReactComponent as UpcomingIcon } from '../../../../../../../../../img/icon-upcoming.svg';
import { ReactComponent as ActionsIcon } from '../../../../../../../../../img/task-teaser-actions-expand.svg';

import {
  STATUS_PENDING,
  STATUS_SOLVED,
  STATUS_CANNOT_DO,
  STATUS_DISMISS,
  STATUS_WO,
  STATUS_FUTURE,
  STATUS_UPCOMING,
  STATUS_APPROVAL_IN_PROGRESS,
  STATUS_APPROVAL_APPROVED,
  STATUS_APPROVAL_REJECTED,
  STATUS_APPROVAL_RETURN_TO_SUBMITTER
} from '../../../../../../../../Services/api/ActionItems';

// Import images
import { ReactComponent as UnCompleteIcon } from '../../../../../../../../../img/task-teaser/action-deactivate.svg';
import { ReactComponent as UserIcon } from '../../../../../../../../../img/user.svg';
import ConfigService from '../../../../../../../../Services/api/Config';

import Utils from '../../../../../../../../Services/Utils';
import Icon from '../../../../../../../Common/Icon';
import Tooltip from '../../../../../../../Common/Tooltip';
import Checkbox from '../../../../../../../FormFields/Checkbox';
import CalendarTaskPopup from './CalendarTaskPopup';
// styles
import './style.scss';

export const WORKFLOW_STATUS_PENDING = 0;
export const WORKFLOW_STATUS_STARTED = 1;
export const WORKFLOW_STATUS_PAUSED = 2;
export const WORKFLOW_STATUS_STOPPED = 3;
export const WORKFLOW_STATUS_SOLVED = 4;
export const WORKFLOW_STATUS_CANNOT_DO = 5;

export const MRB_STATUS_SCHEDULED = 0;
export const MRB_STATUS_OVERDUE = 1;
export const MRB_STATUS_UPCOMING = 2;
export const MRB_STATUS_COMPLETED = 3;
export const MRB_STATUS_CANNOT_DO = 4;

/**
 * @callback onActionCb
 * @param {string} action
 * @param {Object} task
 */
/**
 * @callback onExpandCb
 * @param {boolean} expanded
 */
/**
 * Workflow task
 * @param {moment} day
 * @param {[Moment, Moment]} range
 * @param {MRB.ActionItem} task
 * @param {boolean} expanded
 * @param {onActionCb} onAction
 * @param {onExpandCb} onExpand
 */
const CalendarTask = ({ day, range, task, expanded, onExpand, onAction, isPermissionAvailable, moveInDate, actionItems }) => {
  const {
    status,
    mrb_status,
    workflow_status,
    name,
    dynamic_form_id,
    dynamic_form_data_id,
    notes,
    attachments,
    start_date,
    expected_completion_date,
    track_time,
    users_assigned,
    vendors,
    level_position
  } = task;
  const [actions, setActions] = useState(false);

  const handleActions = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setActions((act) => !act);
  }, []);

  const handleAction = useCallback((e, action) => {
    e.preventDefault();
    e.stopPropagation();
    onAction(action, task, moveInDate, actionItems);
  }, [task, onAction]);

  const getDuration = useCallback(() => {
    let duration = 0
    const numberDaysTask = moment(expected_completion_date).diff(moment(start_date), 'days') + 1;
    const diffFromStart = day.diff(moment(start_date), 'days');
    const diffToEnd = moment(expected_completion_date).diff(moment(range[1]), 'days');
    const isBefore = moment(start_date).isBefore(moment(range[0]), 'day')
    const isAfter = moment(expected_completion_date).isAfter(moment(range[1]), 'day');

    if (isBefore) {
      duration = numberDaysTask - diffFromStart;
    }
    if (isAfter) {
      duration = numberDaysTask - diffToEnd;
    }
    if (!isBefore && !isAfter) {
      duration = numberDaysTask;
    }
    return duration;
  }, [expected_completion_date, day, start_date, range]);


  const isTaskAvailable = useCallback(() => {
    const today  = moment();
    const startDate = moment(start_date);
  
    return moment(startDate).isSameOrBefore(today);
  }, [ start_date]);

  const getWidth = useCallback(() => {
    const duration = getDuration();
    const width = duration > 1 ? 200 * getDuration() : 200;
    return width + 'px';
  }, [getDuration]);

  const getDaysLeft = useCallback(() => {
    let days = 0
    const isAfter = moment(start_date).isSameOrAfter(moment(range[0]), 'day')
    const diffFromStart = moment(start_date).diff(moment(range[0]), 'days');
    if (isAfter) {
      days = diffFromStart;
    }
    return days;
  }, [start_date, range]);

  const getLeft = useCallback(() => {
    const days = getDaysLeft();
    const left = days >= 1 ? 200 * getDaysLeft() : 0;
    return left + 'px';
  }, [getDaysLeft]);

  const getTop = useCallback(() => {
    const multi = Boolean(expanded) ? 89 : 46
    const level_position_px = level_position > 1 ? multi * (level_position - 1) : 0;
    return level_position_px + 'px';
  }, [level_position, expanded]);

  const getClasses = useCallback(() => {
    const classes = [];
    const end = !expected_completion_date ? day : moment(expected_completion_date);
    const start = moment(start_date);
    if (end.format('YYYY-MM-DD') > range[1]) {
      classes.push('TimelineTask_not_ended');
    }
    if (start.format('YYYY-MM-DD') < range[0]) {
      classes.push('TimelineTask_not_started');
    }
    return classes.join(' ');
  }, [expected_completion_date, day, start_date, range]);

  // eslint-disable-next-line max-params
  const renderIcon = useCallback((icon, color, width = 15, height = 15) => {
    return (
      <Icon width={width} height={height} color={color || 'secondaryColor'}>
        {icon}
      </Icon>
    );
  }, []);

  const dynamicIcon = useMemo(() => {
    return dynamic_form_data_id ? (
      <DynamicFormDataIcon alt="dynamic-form-data" onClick={(e) => handleAction(e, 'resolve')} />
    ) : (
      <DynamicFormIcon alt="dynamic-form" onClick={(e) => handleAction(e, 'resolve')} />
    );
  }, [dynamic_form_data_id, handleAction]);

  const statusIcon = useMemo(() => {
    switch (mrb_status) {
      case MRB_STATUS_OVERDUE:
        return <OverdueIcon alt="overdue" />;
      case MRB_STATUS_COMPLETED:
        return <CompletedIcon alt="done" />;
      case MRB_STATUS_UPCOMING:
        return <ProgressIcon alt="progress" />;
      case MRB_STATUS_SCHEDULED:
        return <UpcomingIcon alt="upcoming" />;
      case MRB_STATUS_CANNOT_DO:
        return <UnCompleteIcon alt="blocked" />;
      default:
        return <CompletedIcon alt="done" />;
    }
  }, [mrb_status]);

  const statusColor = useCallback(() => {
    switch (mrb_status) {
      case MRB_STATUS_OVERDUE:
        return 'errorColor';
      case MRB_STATUS_COMPLETED:
        return 'tertiaryColor';
      case MRB_STATUS_UPCOMING:
        return 'warningColor';
      case MRB_STATUS_SCHEDULED:
        return 'secondaryColor';
      case MRB_STATUS_CANNOT_DO:
        return 'auxColor';
      default:
        return 'primaryColor';
    }
  }, [mrb_status]);

  const getName = useCallback((user) => {
    if (!user) {
      return '';
    }
    return (
      <>
        <span>
          {user.first_name} {user.last_name}
        </span>
        <span>{user.email}</span>
      </>
    );
  }, []);

  const getInitials = useCallback((user) => {
    if (!user) {
      return '';
    }
    const initials = [];
    if (user.first_name) {
      initials.push(user.first_name.substring(0, 1));
    }
    if (user.last_name) {
      initials.push(user.last_name.substring(0, 1));
    }
    return initials.join('');
  }, []);

  const userInitials = useCallback(
    (user, backgroundColor, vendor) => {
      const icon = vendor ? <ContractorIcon onClick={(e) => handleAction(e, 'user')} alt="vendor" /> : <UserIcon onClick={(e) => handleAction(e, 'user')} alt="user" />;
      return (
        <Tooltip message={getName(user)} className="Tooltip_Blue" key={user.id}>
          <span className="TimelineTask__bottom-tag" style={{ background: ConfigService.getColor(backgroundColor || 'secondaryColor') }}>
            {renderIcon(icon, 'white', 10, 10)}
            {getInitials(user)}
          </span>
        </Tooltip>
      );
    },
    [getName, renderIcon, getInitials]
  );

  return (
    <>
      <div 
        onClick={() => onExpand(!expanded)}
        className={classNames('TimelineTask', getClasses(), { TimelineTask_open: Boolean(expanded) })}
        style={{ borderColor: ConfigService.getColor(statusColor()), width: getWidth(), left: getLeft(), top: getTop() }}>
        <div className="TimelineTask__title">
          {renderIcon(statusIcon, statusColor())}
          <span className="Pointer" onClick={(e) => handleAction(e, 'taskDetail')} >
            {name}
          </span>
          {(isTaskAvailable() && !track_time && [STATUS_PENDING, STATUS_WO, STATUS_APPROVAL_APPROVED, STATUS_APPROVAL_RETURN_TO_SUBMITTER].includes(status) && (
            <Checkbox label="" className="Pointer" onClick={(e) => handleAction(e, 'resolve')} />
          ))}
          {(track_time && ![STATUS_SOLVED, STATUS_CANNOT_DO, STATUS_DISMISS, STATUS_FUTURE, STATUS_UPCOMING, STATUS_APPROVAL_REJECTED, STATUS_APPROVAL_IN_PROGRESS].includes(status) && (
            <>
              {isTaskAvailable() && ![WORKFLOW_STATUS_STARTED, WORKFLOW_STATUS_SOLVED, WORKFLOW_STATUS_CANNOT_DO].includes(workflow_status) &&
                renderIcon(<PlayIcon alt="play" onClick={(e) => handleAction(e, 'play')} />)}
              {isTaskAvailable() && [WORKFLOW_STATUS_STARTED].includes(workflow_status) && renderIcon(<PauseIcon alt="pause" onClick={(e) => handleAction(e, 'pause')} />)}
              {isTaskAvailable() && [WORKFLOW_STATUS_STARTED].includes(workflow_status) &&
                renderIcon(<ResolveIcon alt="resolve" onClick={(e) => handleAction(e, 'resolve')} />)}
            </>
          ))}

          {isTaskAvailable() && [STATUS_SOLVED, STATUS_CANNOT_DO].includes(status) &&
            renderIcon(<UnCompleteIcon alt="unComplete" onClick={(e) => handleAction(e, 'unComplete')} />)}
        </div>
        {expanded && (
          <>
            <div className="TimelineTask__body">
              {isTaskAvailable() && dynamic_form_id && renderIcon(dynamicIcon)}
              {!Utils.isEmpty(attachments) && renderIcon(<AttachmentsIcon alt="attachments" onClick={(e) => handleAction(e, 'attachments')} />)}
              {!Utils.isEmpty(notes) && renderIcon(<NoteIcon alt="notes" onClick={(e) => handleAction(e, 'notes')} />, 'auxColor')}
              {renderIcon(<ActionsIcon alt="actions" onClick={handleActions} />, 'primaryColor')}
            </div>
            {(!Utils.isEmpty(users_assigned) || !Utils.isEmpty(vendors)) && (
              <div className="TimelineTask__bottom-tags">
                {users_assigned?.map((user) => userInitials(user))}
                {vendors?.map((vendor) => userInitials(vendor, 'primaryColor', true))}
              </div>
            )}
          </>
        )}
        {actions && <CalendarTaskPopup onActions={handleActions} onAction={handleAction} status={status} isPermissionAvailable={isPermissionAvailable} task={task}/>}
      </div>
    </>
  );
};

CalendarTask.propTypes = {
  day: PropTypes.any,
  range: PropTypes.array,
  expanded: PropTypes.bool,
  task: PropTypes.shape({
    status: PropTypes.number,
    mrb_status: PropTypes.number,
    workflow_status: PropTypes.number,
    name: PropTypes.string.isRequired,
    track_time: PropTypes.bool,
    start_date: PropTypes.string,
    expected_completion_date: PropTypes.string,
    attachments: PropTypes.array,
    notes: PropTypes.array,
    dynamic_form_data_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    dynamic_form_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    users_assigned: PropTypes.array,
    vendors: PropTypes.array,
    level_position: PropTypes.number
  }),
  onExpand: PropTypes.func,
  onAction: PropTypes.func.isRequired,
  isPermissionAvailable: PropTypes.func,
  moveInDate: PropTypes.any,
  actionItems: PropTypes.any
};

export default CalendarTask;
